import { Link, useLocation } from 'react-router';
import { casePath, isArchivedCase, type JobOrder, type ViewKey } from '@/domain';
import { useCaseRecord } from '@/application/usecases/useArchive';
import { useJobOrder } from '@/application/usecases/useOrders';
import { useSession } from '@/application/usecases/useSession';
import { strings } from '@/shared/strings';
import { EmptyState } from '@/ui/feedback/EmptyState';
import { SkeletonCase } from '@/ui/feedback/Loading';
import { GoIcon, OrdersIcon } from '@/ui/icons';
import { Drawer } from '@/ui/layout/Drawer';
import { usePhoneLayout } from '@/ui/layout/usePhoneLayout';
import { FullScreenPage } from '@/ui/primitives/FullScreenPage';
import { useOverlay } from '@/ui/primitives/useOverlay';
import { CaseRecordView } from '@/features/archive/CaseRecordView';
import { OrderCaseView, type CaseBackTo } from './OrderCaseView';
import { useCaseInPlace } from './useCaseInPlace';
import styles from './CaseInPlace.module.css';

export interface CaseInPlaceProps {
  /**
   * What the page under the case is called, for the way back from a screen the
   * case leads to: the doctor's or the patient's name, as the record's own
   * Back has said since cases were first listed on a profile.
   */
  returnLabel: string;
  /**
   * The word in the address this panel answers to, where it is not `case`: the
   * shell's own, for a case a notification opened.
   */
  param?: string;
}

/**
 * The case open over a doctor's or a patient's page. The request of
 * 6 October 2026 (fifth), section 3: "open the case right there, in a side
 * drawer on desktop and tablet, full screen on mobile, with a back button".
 *
 * The page draws this once, beside its tables. It reads the case from the
 * page's address (`useCaseInPlace`), so a row that is pressed, a link somebody
 * was sent and the browser's Back all arrive here the same way. Above the
 * phone's width it is the drawer Orders opens a case in; on a phone it is a
 * page over the whole screen with a back arrow, as Lab Notes is. Either way it
 * lies OVER the page, which stays mounted, so closing it shows the page as it
 * was left.
 *
 * Inside is the case as the reader's screens show it. A case in hand, for
 * somebody who holds Orders, is the order's own view with every action on it.
 * A case that has left the lab or was archived is its record, and so is any
 * case for somebody who holds the record and not Orders. Above it is the way
 * to the case's own screen, "Open in Orders" or "Open the record", by
 * `casePath`.
 *
 * Nothing opens for a reader who holds neither screen: their rows were never a
 * thing to press, and a link that names a case is no way round that.
 */
export function CaseInPlace({ returnLabel, param }: CaseInPlaceProps) {
  const place = useCaseInPlace(param);
  const location = useLocation();
  const phone = usePhoneLayout();
  const jobOrderNo = place.mayOpen ? place.jobOrderNo : null;

  const body =
    jobOrderNo === null ? null : (
      // Keyed by the case: another case starts with nothing of this one's on screen.
      <CaseBody
        key={jobOrderNo}
        jobOrderNo={jobOrderNo}
        gone={place.gone}
        onClose={place.close}
        // Back to this page with the case still open over it, as it is now.
        backTo={{ path: `${location.pathname}${location.search}`, label: returnLabel }}
      />
    );

  if (phone) {
    if (jobOrderNo === null) return null;
    return (
      <FullScreenPage
        title={jobOrderNo}
        kicker={strings.caseOverlay.kicker}
        onClose={place.close}
        closeLabel={strings.caseOverlay.back}
      >
        {body}
      </FullScreenPage>
    );
  }

  return (
    <Drawer open={jobOrderNo !== null} title={jobOrderNo ?? ''} onClose={place.close}>
      {body}
    </Drawer>
  );
}

/** Whether a case is still the lab's to work: not dispatched, not archived, not deleted. */
function inHand(order: JobOrder): boolean {
  return (
    !order.dispatch.dispatched && !isArchivedCase(order) && order.deletion.status !== 'deleted'
  );
}

interface CaseBodyProps {
  jobOrderNo: string;
  gone: boolean;
  onClose: () => void;
  backTo: CaseBackTo;
}

/**
 * What the panel holds: the order's own view, or the record.
 *
 * While it is on screen the page behind does not scroll, and when it goes,
 * focus returns to the row that opened it (`useOverlay`), so somebody working
 * down a list with the keyboard carries on from where they were.
 *
 * Which of the two it is, is read off the case and not guessed. A holder of
 * Orders asks for the order first, unless the row already said the case has
 * left: an order in hand is shown as one, and anything else (dispatched,
 * archived, or an order the server no longer lists) falls through to the
 * record, which serves every case there has been.
 */
function CaseBody({ jobOrderNo, gone, onClose, backTo }: CaseBodyProps) {
  useOverlay();
  const views = useSession().data?.views ?? [];
  const asOrder = views.includes('orders') && !gone;
  const order = useJobOrder(asOrder ? jobOrderNo : undefined);

  if (asOrder && order.isPending) return <SkeletonCase label={strings.common.loadingCase} />;

  if (asOrder && order.data !== undefined && inHand(order.data.jobOrder)) {
    return (
      <>
        <OwnScreen views={views} jobOrderNo={jobOrderNo} door="order" backTo={backTo} />
        <OrderCaseView jobOrderNo={jobOrderNo} onGone={onClose} backTo={backTo} />
      </>
    );
  }
  return <RecordInPlace views={views} jobOrderNo={jobOrderNo} backTo={backTo} />;
}

/**
 * The record of a case that is not in hand, in the panel. The one record view,
 * told where another case's record opens: it would otherwise work that out
 * from an address that is a doctor's or a patient's page here, and send
 * everybody to the archive.
 */
function RecordInPlace({
  views,
  jobOrderNo,
  backTo,
}: {
  views: readonly ViewKey[];
  jobOrderNo: string;
  backTo: CaseBackTo;
}) {
  const record = useCaseRecord(jobOrderNo);

  if (record.isPending) return <SkeletonCase label={strings.common.loadingCase} />;
  if (record.data === undefined) {
    return (
      <EmptyState
        icon={<OrdersIcon />}
        heading={strings.caseOverlay.notFound}
        detail={strings.caseOverlay.notFoundDetail}
      />
    );
  }

  return (
    <>
      <OwnScreen views={views} jobOrderNo={jobOrderNo} door="record" backTo={backTo} />
      <CaseRecordView
        record={record.data}
        recordPath={(other) => casePath(views, { jobOrderNo: other }, 'record')}
        narrow
      />
    </>
  );
}

/**
 * The way from the panel to the case's own screen, for anyone who wants the
 * full page: "Open in Orders" for a case in hand, "Open the record" for one
 * that is not. Asked of `casePath`, so it leads where this reader's screens
 * do; the words follow where it leads, and there is no link at all where it
 * leads nowhere. The screen it opens is told where Back returns to.
 */
function OwnScreen({
  views,
  jobOrderNo,
  door,
  backTo,
}: {
  views: readonly ViewKey[];
  jobOrderNo: string;
  door: 'order' | 'record';
  backTo: CaseBackTo;
}) {
  const path = casePath(views, { jobOrderNo }, door);
  if (path === null) return null;
  const inOrders = path === `/orders/${jobOrderNo}`;

  return (
    <p className={styles.own}>
      <Link to={path} state={{ backTo }}>
        {inOrders ? strings.caseOverlay.openInOrders : strings.caseOverlay.openRecord}
        <GoIcon />
      </Link>
    </p>
  );
}
